import { Inject, Injectable } from '@angular/core';
import {
    AppNotificationService,
    AppNotificationType,
    AttendeeModel,
    CalibrationModel,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigService,
    CONFIRMATION_TYPES,
    ConfirmationModel,
    CONNECTION_ERROR_CODES,
    CseStockService,
    ENDPOINTS,
    EscalationService,
    isDefined,
    LocalStorageRecipientsService,
    StorageNotificationStatusService,
    MaterialService,
    NOTIFICATION_STATUS_CODE,
    NotificationIdentifier,
    NotificationIdentifierModel,
    NotificationModel,
    NotificationService,
    NotificationUpdateService,
    ODataCalibrationModel,
    OnlineOnlyService,
    PaceState,
    StatusModel,
    TextModel,
    TimeRecordingService,
    TimesheetAndExpensesService,
    UriUtilitiesService,
    NotificationStatusService,
    NotificationCompletedListModel,
} from '@evorait/pace-core';
import {
    cleanupFormList,
    cleanupSpqDocs,
    ConfirmationInterface,
    FormIdentifier,
    FormsService,
    ParticipantCopy,
    ParticipantCopyId,
    ParticipantParticulars,
} from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, take, tap } from 'rxjs/operators';
import { LocalStorageSalesQuoteService } from '../../material/services/local-storage-sales-quote.service';
import { MileageModel } from '../../mileage/models/mileage.model';
import { MileageService } from '../../mileage/services/mileage.service';
import { OdataConfirmationService } from './odata-confirmation.service';
import { StorageConfirmationService } from './storage-confirmation.indexeddb.service';
import { ConfirmationConcurrentStoreService } from './storage-confirmation.concurrent.service';
import { cleanupSafetyAssessmentForm } from 'libs/pace-forms/src/lib/store/actions/safety-assessment.action';

@Injectable({
    providedIn: 'root',
})
export class ConfirmationService implements ConfirmationInterface {
    public readonly confirmationCompletedPreviously$: Observable<void>;
    private readonly confirmationCompletedPreviously = new Subject<void>();

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly configService: ConfigService,
        private readonly formsService: FormsService,
        private readonly storageConfirmationService: StorageConfirmationService,
        private readonly odataConfirmationService: OdataConfirmationService,
        private readonly notificationService: NotificationService,
        private readonly notificationUpdateService: NotificationUpdateService,
        private readonly materialService: MaterialService,
        private readonly timesheetAndExpensesService: TimesheetAndExpensesService,
        private readonly timeRecordingService: TimeRecordingService,
        private readonly escalationService: EscalationService,
        private readonly localStorageSalesQuoteService: LocalStorageSalesQuoteService,
        private readonly cseStockService: CseStockService,
        private readonly mileageService: MileageService,
        private readonly store: Store<PaceState>,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        private readonly storageNotificationStatusService: StorageNotificationStatusService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly localStorageRecipientsService: LocalStorageRecipientsService,
        private readonly confirmationConcurrentStoreService: ConfirmationConcurrentStoreService,
        private readonly notificationStatusService: NotificationStatusService,
    ) {
        this.confirmationCompletedPreviously$ = this.confirmationCompletedPreviously.asObservable();
    }

    public getConfirmation(id: NotificationIdentifierModel, type: CONFIRMATION_TYPES): Observable<ConfirmationModel> {
        this.storageNotificationStatusService.saveConfirmationStarted(id);
        return this.storageConfirmationService.getConfirmation(id, type).pipe(
            map((confirmation) => {
                this.log.debug('ConfirmationService: getConfirmation: Loading saved confirmation');
                // check for confirmation.materials as it could be undefined for APPS user
                if (confirmation.materials && confirmation.materials.listMaterial) {
                    confirmation.materials.listMaterial = this.materialService.updateMaterialListFromLocalStorage(
                        confirmation.notificationIdentifier,
                        confirmation.materials.listMaterial,
                    );
                }
                return confirmation;
            }),
            catchError((error) => {
                this.log.debug(`Creating new confirmation model since it was not possible to load one:`, error);
                return of(new ConfirmationModel(id, type));
            }),
        );
    }

    public getRemoteConfirmation(
        id: NotificationIdentifierModel,
        type: CONFIRMATION_TYPES,
    ): Observable<ConfirmationModel> {
        return this.confirmationConcurrentStoreService.getConfirmation(id, type);
    }

    public getConcurrentConfirmation(
        id: NotificationIdentifierModel,
        type: CONFIRMATION_TYPES,
    ): Observable<ConfirmationModel> {
        return forkJoin([
            this.getConfirmation(id, type),
            this.confirmationConcurrentStoreService.getConfirmation(id, type),
        ]).pipe(
            take(1),
            map(([localConfirmation, remoteConfirmation]) => {
                if (localConfirmation.saveTimestamp < remoteConfirmation.saveTimestamp) {
                    this.saveConfirmation(remoteConfirmation, remoteConfirmation.saveTimestamp);
                    return remoteConfirmation;
                }
                return localConfirmation;
            }),
        );
    }

    public saveConfirmation(confirmation: ConfirmationModel, timestamp: number = Date.now()): Observable<void> {
        confirmation.saveTimestamp = timestamp;
        return this.storageConfirmationService.saveConfirmation(confirmation).pipe(
            mergeMap(() => this.saveMaterials(confirmation)), // save materials to local storage
            mergeMap(() => {
                if (
                    this.configService.getShowDataMaintenanceEnhancement() &&
                    typeof confirmation.serviceReport !== 'undefined'
                ) {
                    return this.localStorageRecipientsService.saveRecipientsByNotificationId(
                        confirmation.serviceReport.existingRecipients.value,
                        confirmation.notificationIdentifier,
                    );
                }
                return of(undefined);
            }),
            mergeMap(() => {
                if (this.configService.getAllowNotificationPause()) {
                    const notification = this.notificationService.getNotificationByIdentifier(
                        confirmation.notificationIdentifier,
                    );
                    if (!notification) {
                        throw new Error(
                            `Could not find notification for identifier '${confirmation.notificationIdentifier.toString()}'`,
                        );
                    }
                    return this.timesheetAndExpensesService
                        .saveTimesheetNotification(
                            notification,
                            [],
                            confirmation.timeRecording.timeRecordingEntries.value,
                            new TextModel('Distance'),
                            false,
                        )
                        .pipe(map(() => undefined));
                }
                return of(undefined);
            }),
        );
    }

    public saveConcurrentConfirmation(
        confirmation: ConfirmationModel,
        timestamp: number = Date.now(),
    ): Observable<void> {
        confirmation.saveTimestamp = timestamp;
        return this.confirmationConcurrentStoreService
            .saveConfirmation(confirmation)
            .pipe(mergeMap((confirm) => this.saveConfirmation(confirm, timestamp)));
    }

    // P58: save distance, expenses and time recording to localStorage for later use in timesheet
    // NGMAA: save timesheet based on current dates and without expenses.
    private saveTimeSheetAndExpenses(
        confirmation: ConfirmationModel,
        notification: NotificationModel,
    ): Observable<void> {
        if (this.clientManager.isNgmP58()) {
            if (!confirmation.timeRecording) {
                throw new Error(`Confirmation did not contain a time recording section!`);
            }
            const expenses =
                confirmation.expenses && confirmation.expenses.expenses ? confirmation.expenses.expenses.value : [];
            const distance =
                confirmation.expenses && confirmation.expenses.distance
                    ? confirmation.expenses.distance
                    : new TextModel('Distance');
            const isAfterHoursEnabled = this.configService.getEnabledAfterHoursCallOut();
            const afterHourValue = isAfterHoursEnabled
                ? (confirmation.timeRecording.afterHourCallOut.model.value ?? false)
                : false;
            return this.timesheetAndExpensesService
                .saveTimesheetNotification(
                    notification,
                    expenses,
                    confirmation.timeRecording.timeRecordingEntries.value,
                    distance,
                    true,
                    afterHourValue,
                )
                .pipe(
                    mergeMap(() => this.timesheetAndExpensesService.saveCurrentTimesheet()),
                    map(() => undefined),
                );
        } else if (this.clientManager.isNgmAP0()) {
            if (!confirmation.timeRecording) {
                throw new Error(`Confirmation did not contain a time recording section!`);
            }
            return this.timesheetAndExpensesService
                .saveTimesheetNotification(
                    notification,
                    [],
                    confirmation.timeRecording.timeRecordingEntries.value,
                    new TextModel('Distance'),
                    true,
                )
                .pipe(
                    mergeMap(() => this.timesheetAndExpensesService.saveCurrentTimesheet()),
                    map(() => undefined),
                );
        }
        return of(undefined);
    }

    private saveMileageInformation(confirmation: ConfirmationModel): Observable<void> {
        const useMileageHistory = this.configService.getUseMileageHistory();
        if (useMileageHistory) {
            if (!confirmation.expenses) {
                throw new Error(`Confirmation did not contain an expenses section!`);
            } else if (confirmation.expenses.distance.value.length > 0) {
                const mileageModel = new MileageModel(
                    confirmation.expenses.notificationId,
                    confirmation.expenses.distance.value,
                    confirmation.expenses.mileageTravelStart.selectedEntry.value,
                    confirmation.expenses.mileageTravelEnd,
                    moment().startOf('day'),
                );
                return this.mileageService.addMileage(mileageModel).pipe(map(() => undefined));
            }
        }
        return of(undefined);
    }

    public filterConfirmationStatusComplete(notificationId: string): Observable<NotificationCompletedListModel> {
        return this.loadRecentlyCompletedConfirmations().pipe(
            filter((completedNotifications) => {
                if (!completedNotifications.completedNotifications[notificationId]) {
                    return true;
                }
                this.confirmationCompletedPreviously.next();
                return false;
            }),
        );
    }

    public submitConfirmation(confirmation: ConfirmationModel): Observable<void> {
        const now = moment();
        const potentialNotification = this.notificationService.getNotificationByIdentifier(
            confirmation.notificationIdentifier,
        );

        if (!potentialNotification) {
            throw new Error(
                `Could not find notification for identifier '${confirmation.notificationIdentifier.toString()}'`,
            );
        }

        const notification = potentialNotification;
        return this.filterConfirmationStatusComplete(notification.identifier.toString()).pipe(
            mergeMap(() => this.saveConcurrentConfirmation(confirmation)),
            mergeMap(() => this.saveTimeSheetAndExpenses(confirmation, notification)),
            mergeMap(() => this.saveMileageInformation(confirmation)),
            mergeMap(() => this.odataConfirmationService.saveConfirmation(confirmation)),
            catchError((error) => {
                this.log.error(
                    `ConfirmationService: submitConfirmation:
                        ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                        ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                        : <string>this.translate.instant('CONFIRMATION_SUMMARY_SUBMIT_ERROR_TITLE'),
                    error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                        ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                        : <string>this.translate.instant('CONFIRMATION_SUMMARY_SUBMIT_ERROR_BODY'),
                );
                return throwError(error);
            }),
            // update notification status to CRM and save in localStorage
            mergeMap(() => {
                const statusModel = new StatusModel(NOTIFICATION_STATUS_CODE.CRM, now, now);
                return this.notificationService.saveStatus(notification, statusModel);
            }),
            // check if notification status is not saved correctly
            tap(() => {
                const status = this.notificationService.getStatus(notification).statusCode;
                if (status !== NOTIFICATION_STATUS_CODE.CRM) {
                    throw new Error(
                        `ConfirmationService: submitConfirmation: saveStatus: Notification status should be ${NOTIFICATION_STATUS_CODE.CRM} instead of ${status}`,
                    );
                }
            }),
            mergeMap(() => this.postConfirmationSaveAndCleanup(confirmation, notification)),
        );
    }

    public confirmationCompletedPreviouslyCleanup(confirmation: ConfirmationModel): Observable<boolean> {
        const potentialNotification = this.notificationService.getNotificationByIdentifier(
            confirmation.notificationIdentifier,
        );
        this.storageNotificationStatusService.removeConfirmationStarted(confirmation.notificationIdentifier);
        if (!potentialNotification) {
            return this.confirmationConcurrentStoreService
                .removeConfirmation(confirmation)
                .pipe(mergeMap(() => this.storageConfirmationService.removeConfirmation(confirmation)));
        }
        const notification = potentialNotification;
        this.storageNotificationStatusService.removeConfirmationStarted(confirmation.notificationIdentifier);
        const now = moment();
        const statusModel = new StatusModel(NOTIFICATION_STATUS_CODE.CRM, now, now);
        return this.notificationService.saveStatus(notification, statusModel).pipe(
            mergeMap(() => this.confirmationConcurrentStoreService.removeConfirmation(confirmation)),
            mergeMap(() => this.storageConfirmationService.removeConfirmation(confirmation)),
        );
    }

    private postConfirmationSaveAndCleanup(
        confirmation: ConfirmationModel,
        notification: NotificationModel,
    ): Observable<void> {
        const mainEquipment = notification.getMainEquipment();
        if (typeof mainEquipment === 'undefined') {
            throw new Error(
                `ConfirmationService: postConfirmationSaveAndCleanup: Notification '${confirmation.notificationIdentifier.toString()}' has no main equipment!`,
            );
        }
        return this.saveMaterials(confirmation).pipe(
            mergeMap(() => this.notificationService.handlePausedNotifications()),
            // remove locally saved confirmation model
            mergeMap(() => {
                confirmation.isSubmitted = true;
                this.storageNotificationStatusService.removeConfirmationStarted(confirmation.notificationIdentifier);
                return this.confirmationConcurrentStoreService
                    .removeConfirmation(confirmation)
                    .pipe(mergeMap(() => this.storageConfirmationService.removeConfirmation(confirmation)));
            }),
            mergeMap(() => this.notificationStatusService.saveRecentlyCompletedNotifications(notification)),
            mergeMap(() =>
                this.localStorageRecipientsService.removeRecipientsFromLocalStorage(notification.identifier),
            ),
            // stop escalation timeout
            // remove all what's-new for notification
            // remove the cancellation-confirmation information for this notification
            // remove locally saved sales quote information
            // P58: update bootstock materials
            tap(() => {
                this.escalationService.removeEscalationTimer(notification);
                this.notificationUpdateService.removeNotificationUpdates(notification.identifier);
                this.notificationService.removeCancellationConfirmationForInprogress();
                if (this.clientManager.isNgmP58()) {
                    this.localStorageSalesQuoteService.removeSalesQuoteFromLocalStorage(notification.identifier);
                    const bootStockMaterials = this.materialService.getAllBootStockMaterialsForNotification(
                        notification.identifier,
                    );
                    this.cseStockService.setLocalBootStockMaterialModels(bootStockMaterials);
                }
            }),
            // cleanup downloaded forms
            mergeMap(() => {
                if (this.clientManager.isPaceUS() || this.clientManager.isNgmP58() || notification.isPaceFormsEnabled) {
                    return this.formsService
                        .getFormList(
                            mainEquipment.matnr.value,
                            mainEquipment.equnr.value,
                            {
                                frontId: notification.identifier.frontId,
                                qmNum: notification.identifier.qmNum,
                                maNum: notification.identifier.maNum,
                            },
                            true,
                        )
                        .pipe(
                            take(1),
                            filter(isDefined),
                            map((formList) => {
                                this.store.dispatch(cleanupFormList({ formList }));
                                return undefined;
                            }),
                            catchError(() => {
                                this.log.debug(
                                    `ConfirmationService: postConfirmationSaveAndCleanup: Form list cleanup not necessary since there is no list`,
                                );
                                return of(undefined);
                            }),
                            finalize(() => {
                                // Logic to clean up all the SPQ data for the notification
                                if (
                                    this.configService.getDisplayServicePartQuestionnaire() &&
                                    confirmation.materials &&
                                    confirmation.materials.listMaterial.value.length > 0
                                ) {
                                    const spqMaterials = confirmation.materials.listMaterial.value
                                        .filter((material) => material.showServicePartQuestionnaire())
                                        .map((material) => {
                                            const form: FormIdentifier = {
                                                equipmentId: mainEquipment.equnr.value,
                                                formId: material.spqDocId,
                                                materialId: material.materialIdentifier.materialNumber.value,
                                            };
                                            const notificationId: NotificationIdentifier = {
                                                qmNum: material.notificationIdentifier.qmNum,
                                                maNum: material.notificationIdentifier.maNum,
                                                frontId: material.notificationIdentifier.frontId,
                                            };
                                            return { form, notificationId, materialKey: material.materialKey };
                                        });

                                    this.log.debug(
                                        `ConfirmationService: postConfirmationSaveAndCleanup: cleaning up spq data`,
                                    );
                                    this.store.dispatch(cleanupSpqDocs({ spqMaterials }));
                                }
                            }),
                        );
                }
                return of(undefined);
            }),
            mergeMap(() => {
                // Logic to clean up all the Safety assessment form data for the notification
                const safetyAssessmentFormId = this.configService.getSafetyAssessmentForm();
                const formIdentifier = {
                    formId: safetyAssessmentFormId,
                    equipmentId: mainEquipment?.equnr.value,
                    materialId: mainEquipment?.matnr.value,
                };

                return this.formsService.checkSafetyAssessmentFormInCache(formIdentifier, notification.identifier).pipe(
                    map((hasSAFormInCache) => {
                        if (hasSAFormInCache) {
                            this.store.dispatch(
                                cleanupSafetyAssessmentForm({
                                    form: formIdentifier,
                                    notification: notification.identifier,
                                }),
                            );
                        }
                        return undefined;
                    }),
                );
            }),
            catchError((error) => {
                this.log.error(
                    `ConfirmationService: postConfirmationSaveAndCleanup: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return of(undefined);
            }),
        );
    }

    /**
     * save updated materials from confirmation screen to storage
     *
     * @param confirmation the confirmation model
     * @returns {Observable<void>} observable that completes when the materials are saved
     * @throws {Error} if the notification is not found
     */
    private saveMaterials(confirmation: ConfirmationModel): Observable<void> {
        if (!confirmation.materials || confirmation.materials.listMaterial.value.length === 0) {
            // no materials - no saving
            return of(undefined);
        }
        const notification = this.notificationService.getNotificationByIdentifier(confirmation.notificationIdentifier);

        if (!notification) {
            throw new Error(
                `Unable to load notification for confirmation of notification '${confirmation.notificationIdentifier.toString()}'`,
            );
        }

        const isConfirmationSubmitted =
            this.notificationService.getStatus(notification).statusCode === NOTIFICATION_STATUS_CODE.CRM;

        // The activity date shall be the OSR End date if available, else the date where the CRM was send to SAP, for material history req.
        let materialConsumedDate = moment();
        const osrEntries = this.timeRecordingService.getOSREntries(
            confirmation.timeRecording.timeRecordingEntries.value,
        );
        if (osrEntries.length > 0) {
            const lastEntry = this.timeRecordingService.getLastTimeRecordingEntry(osrEntries);
            if (lastEntry) materialConsumedDate = lastEntry.endDateTime.clone();
        }
        return this.materialService.saveConfirmationMaterialsToLocalStorage(
            confirmation.materials.listMaterial.value,
            isConfirmationSubmitted,
            materialConsumedDate,
        );
    }

    // this function is being called when the user is removing a participant from pace forms.
    // We need to remove the corresponding attendee from the attendee list for the participant
    public removeAttendee(
        notificationId: NotificationIdentifier,
        participant: ParticipantCopyId,
    ): Observable<ParticipantCopyId> {
        if (!this.clientManager.isPaceUS()) {
            return throwError(`ConfirmationService: removeAttendee: Method should be called only from PACE US`);
        }

        const notificationIdentifier = new NotificationIdentifierModel(
            notificationId.frontId,
            notificationId.qmNum,
            notificationId.maNum,
        );
        return this.getConcurrentConfirmation(notificationIdentifier, CONFIRMATION_TYPES.NORMAL).pipe(
            mergeMap((confirmation) => {
                if (typeof confirmation.attendees !== 'undefined') {
                    const index = confirmation.attendees.attendeeList.value.findIndex(
                        (attendee) => attendee.id === participant,
                    );

                    confirmation.attendees.attendeeList.removeModel(index);
                }
                return this.saveConcurrentConfirmation(confirmation);
            }),
            map(() => participant),
        );
    }

    public saveAttendee(notificationId: NotificationIdentifier, participant: ParticipantCopy): Observable<void> {
        if (!this.clientManager.isPaceUS()) {
            return throwError(`ConfirmationService: saveAttendee: Method should be called only from PACE US`);
        }

        const notificationIdentifier = new NotificationIdentifierModel(
            notificationId.frontId,
            notificationId.qmNum,
            notificationId.maNum,
        );
        return this.getConcurrentConfirmation(notificationIdentifier, CONFIRMATION_TYPES.NORMAL).pipe(
            mergeMap((confirmation) => {
                if (typeof confirmation.attendees !== 'undefined') {
                    const newAttendee = new AttendeeModel(
                        participant.selectedTitle,
                        participant.title,
                        participant.firstName,
                        participant.lastName,
                        participant.email,
                        participant.notReceivingCredit,
                        participant.signFlag ? 'Y' : 'N',
                        participant.id,
                        participant.submittedOn,
                    );

                    const attendeeIndex = confirmation.attendees.attendeeList.value.findIndex(
                        (attendee) => attendee.id === newAttendee.id,
                    );

                    if (attendeeIndex > -1) {
                        confirmation.attendees.attendeeList.value[attendeeIndex].title.selectByValue(
                            participant.selectedTitle,
                        );
                        confirmation.attendees.attendeeList.value[attendeeIndex].textTitle.value = participant.title;
                        confirmation.attendees.attendeeList.value[attendeeIndex].firstName.value =
                            participant.firstName;
                        confirmation.attendees.attendeeList.value[attendeeIndex].lastName.value = participant.lastName;
                        confirmation.attendees.attendeeList.value[attendeeIndex].mail.value = participant.email;
                        confirmation.attendees.attendeeList.value[attendeeIndex].notReceivingCredit.value =
                            participant.notReceivingCredit;
                        confirmation.attendees.attendeeList.value[attendeeIndex].signFlag.value = participant.signFlag
                            ? 'Y'
                            : 'N';
                        confirmation.attendees.attendeeList.value[attendeeIndex].formSubmitted =
                            participant.submittedOn;
                    } else {
                        confirmation.attendees.attendeeList.addModel(newAttendee);
                    }
                }

                return this.saveConcurrentConfirmation(confirmation);
            }),
        );
    }

    // Only used in PACE US
    public getParticipantsFromAttendees(notificationId: NotificationIdentifier): Observable<ParticipantParticulars[]> {
        if (!this.clientManager.isPaceUS()) {
            return throwError(
                `ConfirmationService: getParticipantsFromAttendees: Method should be called only from PACE US`,
            );
        }
        const notificationIdentifier = new NotificationIdentifierModel(
            notificationId.frontId,
            notificationId.qmNum,
            notificationId.maNum,
        );
        return this.getConcurrentConfirmation(notificationIdentifier, CONFIRMATION_TYPES.NORMAL).pipe(
            map((confirmation) => {
                if (typeof confirmation.attendees === 'undefined') {
                    throw new Error(
                        `ConfirmationService: getParticipantsFromAttendees: Unable to load attendees from confirmation for notification ${JSON.stringify(
                            notificationId,
                        )}`,
                    );
                }

                return confirmation.attendees.attendeeList.value.map((attendee) => ({
                    id: attendee.id,
                    email: attendee.mail.value,
                    selectedTitle: attendee.title.selectedEntry.value,
                    selectedInParticipantCopiesModal: false,
                    title: attendee.textTitle.value,
                    firstName: attendee.firstName.value,
                    lastName: attendee.lastName.value,
                    notReceivingCredit: attendee.notReceivingCredit.value,
                }));
            }),
        );
    }

    public getCalibrationDate(measurementToolId: string): Observable<CalibrationModel> {
        let query = ENDPOINTS.MeasToolSet.toString();
        const queryFilter = `Equipment eq '${measurementToolId}'`;
        query = this.uriUtilities.appendFilterValue(query, queryFilter);
        return this.onlineOnlyService.get(query).pipe(
            map((response: ODataCalibrationModel[]) => {
                if (response.length < 1) {
                    throw new Error(`ConfirmationService: getCalibrationDate: Could not get Calibration Date!`);
                }
                return CalibrationModel.fromODataCalibrationDate(response[0]);
            }),
        );
    }

    public loadRecentlyCompletedConfirmations(): Observable<NotificationCompletedListModel> {
        return this.notificationStatusService.loadRecentlyCompletedNotifications().pipe(take(1));
    }

    public triggerConfirmationCompletedPreviously(): void {
        this.confirmationCompletedPreviously.next();
    }

    public signLater(notification: NotificationModel): void {
        const now = moment();
        let nextStatus: NOTIFICATION_STATUS_CODE;
        switch (notification.updatedStatus.statusCode) {
            case NOTIFICATION_STATUS_CODE.ROSR:
                nextStatus = NOTIFICATION_STATUS_CODE.RTOBS;
                break;
            case NOTIFICATION_STATUS_CODE.FCC:
                nextStatus = NOTIFICATION_STATUS_CODE.FTOBS;
                break;
            case NOTIFICATION_STATUS_CODE.RTOBS:
            case NOTIFICATION_STATUS_CODE.FTOBS:
                nextStatus = notification.updatedStatus.statusCode;
                break;
            default:
                nextStatus = NOTIFICATION_STATUS_CODE.TOBS;
                break;
        }
        const statusModel = new StatusModel(nextStatus, now, now);
        this.notificationService
            .saveStatus(notification, statusModel)
            .pipe(
                mergeMap(() => this.notificationService.savePausedIndicator([notification], false)),
                catchError((error) => {
                    this.log.error(
                        'Error while saving notification status for sign later: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('CONFIRMATION_SAVE_ERROR_TITLE'),
                        <string>this.translate.instant('CONFIRMATION_SAVE_ERROR_BODY'),
                    );
                    return of(undefined);
                }),
                take(1),
            )
            .subscribe();
    }
}
